import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Badge,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Add,
  Remove,
  ShoppingCart,
  AccessTime,
  CheckCircle,
  Restaurant,
  ArrowBack,
  Send,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { TableService } from '../services/tableService.js';
import { TableSessionService } from '../services/tableSessionService.js';
import { CustomerOrderService } from '../services/customerOrderService.js';
import karobarLogo from '../assets/logo_white.png';

// ═══ Local Storage Cart Helpers ═══
const getCartKey = (slug, tableNo) => `karobar_cart_${slug}_${tableNo}`;
const getNameKey = (slug, tableNo) => `karobar_name_${slug}_${tableNo}`;

const loadCartFromStorage = (slug, tableNo) => {
  try {
    const stored = localStorage.getItem(getCartKey(slug, tableNo));
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
};

const saveCartToStorage = (slug, tableNo, cart) => {
  try {
    localStorage.setItem(getCartKey(slug, tableNo), JSON.stringify(cart));
  } catch { /* ignore */ }
};

const clearCartFromStorage = (slug, tableNo) => {
  try {
    localStorage.removeItem(getCartKey(slug, tableNo));
  } catch { /* ignore */ }
};

const loadNameFromStorage = (slug, tableNo) => {
  try {
    return localStorage.getItem(getNameKey(slug, tableNo)) || '';
  } catch { return ''; }
};

const saveNameToStorage = (slug, tableNo, name) => {
  try {
    localStorage.setItem(getNameKey(slug, tableNo), name);
  } catch { /* ignore */ }
};

// ═══ Sub-components for each step ═══

const TableOccupied = ({ outletName }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Restaurant sx={{ fontSize: 80, color: '#FF9800', mb: 2 }} />
    <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
      Table Occupied
    </Typography>
    <Typography color="text.secondary">
      This table currently has an active order at {outletName}.
    </Typography>
    <Typography color="text.secondary" fontSize="0.85rem" sx={{ mt: 2 }}>
      Please ask the staff for assistance.
    </Typography>
  </Box>
);

const CustomerLanding = ({ outletName, tableNumber, onSubmit, loading: submitting, initialName }) => {
  const [name, setName] = useState(initialName || '');

  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 3, maxWidth: 400, mx: 'auto' }}>
      <Restaurant sx={{ fontSize: 64, color: '#4527A0', mb: 2 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {outletName}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Table {tableNumber}
      </Typography>
      <TextField
        fullWidth
        label="Your Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ mb: 2 }}
        autoFocus
      />
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={!name.trim() || submitting}
        onClick={() => onSubmit(name.trim())}
        sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700, fontSize: '1rem', textTransform: 'none' }}
      >
        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Browse Menu'}
      </Button>
    </Box>
  );
};

const CustomerWaiting = ({ outletName }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <CircularProgress size={48} sx={{ color: '#4527A0', mb: 3 }} />
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
      Waiting for Approval
    </Typography>
    <Typography color="text.secondary" fontSize="0.9rem">
      The staff at {outletName} will approve your order shortly.
    </Typography>
    <Typography color="text.disabled" fontSize="0.8rem" sx={{ mt: 2 }}>
      Please wait...
    </Typography>
  </Box>
);

const CustomerMenu = ({ menuData, cart, onAddItem, onRemoveItem, onViewCart, showBackButton, onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const items = menuData.items || [];

  const categories = useMemo(() => {
    const cats = new Set();
    items.forEach((item) => {
      (item.category1 || []).forEach((c) => cats.add(c));
    });
    return ['All', ...Array.from(cats).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    if (selectedCategory === 'All') return items;
    return items.filter((item) =>
      (item.category1 || []).some((c) => c.toLowerCase() === selectedCategory.toLowerCase())
    );
  }, [items, selectedCategory]);

  const cartCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', pb: cartCount > 0 ? '80px' : 0 }}>
      {/* Back button when coming from tracking */}
      {showBackButton && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, color: '#4527A0' }}
          >
            Back to Order
          </Button>
        </Box>
      )}
      {/* Category chips */}
      <Box sx={{ px: 2, pt: 2, pb: 1, overflowX: 'auto', position: 'sticky', top: 52, zIndex: 1050, bgcolor: '#FAFAFA' }}>
        <Box sx={{ display: 'flex', gap: 0.75, pb: 0.5 }}>
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat;
            const isVeg = cat.toLowerCase() === 'veg';
            const isNonVeg = cat.toLowerCase() === 'non veg' || cat.toLowerCase() === 'non-veg' || cat.toLowerCase() === 'nonveg';
            const chipColor = isVeg ? '#34C759' : isNonVeg ? '#FF3B30' : null;

            const chipSx = chipColor
              ? {
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  px: 1.5,
                  py: 2.5,
                  borderRadius: '20px',
                  bgcolor: isSelected ? chipColor : `${chipColor}4D`,
                  color: isSelected ? '#FFF' : chipColor,
                  border: `1.5px solid ${chipColor}`,
                  '&:hover': { bgcolor: isSelected ? chipColor : `${chipColor}60` },
                }
              : { fontSize: '0.85rem', fontWeight: 500, px: 1.5, py: 2.5, borderRadius: '20px' };

            return (
              <Chip
                key={cat}
                label={cat}
                size="medium"
                onClick={() => setSelectedCategory(cat)}
                color={chipColor ? undefined : (isSelected ? 'primary' : 'default')}
                variant={chipColor ? 'filled' : (isSelected ? 'filled' : 'outlined')}
                sx={chipSx}
              />
            );
          })}
        </Box>
      </Box>

      {/* Items Grid - responsive cards with 4:5 aspect ratio */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, pb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: 1.5,
          }}
        >
          {filtered.map((item) => {
            const qty = cart[item.item_id] || 0;
            return (
              <Card
                key={item.item_id}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                  aspectRatio: '4 / 5',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                {/* Image area */}
                <Box sx={{ p: 1, flex: '1 1 auto', minHeight: 0 }}>
                  <Box
                    component="img"
                    src={item.image_link || 'https://via.placeholder.com/200x200?text=No+Image'}
                    alt={item.item_name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '12px',
                      objectFit: 'cover',
                    }}
                  />
                </Box>

                {/* Item Name */}
                <Box sx={{ px: 1.5, pt: 0.5 }}>
                  <Typography fontWeight={600} fontSize="0.85rem" noWrap>
                    {item.item_name}
                  </Typography>
                </Box>

                {/* Price + Add button row */}
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography fontWeight={700} fontSize="0.9rem" color="success.main">
                    ₹{item.price}
                  </Typography>
                  {qty > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => onRemoveItem(item.item_id)} sx={{ bgcolor: '#F0F0F0', width: 26, height: 26 }}>
                        <Remove sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Typography fontWeight={700} fontSize="0.8rem" sx={{ minWidth: 16, textAlign: 'center' }}>
                        {qty}
                      </Typography>
                      <IconButton size="small" onClick={() => onAddItem(item)} sx={{ bgcolor: '#F0F0F0', width: 26, height: 26 }}>
                        <Add sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onAddItem(item)}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        minWidth: 50,
                        py: 0.4,
                        px: 1.5,
                        fontWeight: 600,
                        boxShadow: 'none',
                      }}
                    >
                      Add
                    </Button>
                  )}
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* Sticky View Cart Button */}
      {cartCount > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            zIndex: 1000,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onViewCart}
            startIcon={
              <Badge badgeContent={cartCount} color="error">
                <ShoppingCart />
              </Badge>
            }
            sx={{ borderRadius: '12px', py: 1.2, fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}
          >
            View Cart
          </Button>
        </Box>
      )}
    </Box>
  );
};

const CustomerCart = ({ cart, items, onSendOrder, onBack, placing, customerName }) => {
  const cartItems = Object.entries(cart)
    .filter(([, qty]) => qty > 0)
    .map(([itemId, qty]) => {
      const item = items.find((i) => i.item_id === itemId);
      return { ...item, qty };
    })
    .filter((i) => i.item_name);

  const total = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);
  const maxPrepTime = Math.max(...cartItems.map((i) => i.prep_time || 10));

  return (
    <Box sx={{ p: 3, maxWidth: 500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>
          Your Cart
        </Typography>
      </Box>

      {/* Cart Items Table */}
      <Box sx={{ bgcolor: '#FFF', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F3FF' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#4527A0', border: 'none', py: 1.5 }}>Item</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#4527A0', border: 'none', py: 1.5 }}>Qty</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#4527A0', border: 'none', py: 1.5 }}>Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cartItems.map((item, index) => (
              <TableRow key={item.item_id} sx={{ bgcolor: index % 2 === 0 ? '#FAFAFA' : '#FFF', '& td': { border: 'none' } }}>
                <TableCell sx={{ py: 1.5, fontSize: '0.85rem', fontWeight: 500 }}>
                  {item.item_name}
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5, fontSize: '0.85rem' }}>
                  {item.qty}
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, fontSize: '0.85rem', fontWeight: 600 }}>
                  ₹{item.price * item.qty}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Summary Card */}
      <Box sx={{ bgcolor: '#FFF', borderRadius: '16px', p: 2.5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography fontWeight={700} fontSize="1rem">Total</Typography>
          <Typography fontWeight={700} fontSize="1.1rem" color="#4527A0">₹{total}</Typography>
        </Box>
        <Chip
          icon={<AccessTime sx={{ fontSize: 14 }} />}
          label={`Estimated prep time: ~${maxPrepTime} minutes`}
          size="small"
          sx={{ bgcolor: '#F5F3FF', color: '#4527A0', fontWeight: 500 }}
        />
        {customerName && (
          <Typography fontSize="0.8rem" color="text.secondary" sx={{ mt: 1.5 }}>
            Ordering as: <strong>{customerName}</strong>
          </Typography>
        )}
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={placing || cartItems.length === 0}
        onClick={() => onSendOrder(cartItems, total)}
        startIcon={placing ? null : <Send />}
        sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}
      >
        {placing ? <CircularProgress size={24} color="inherit" /> : 'Send Order'}
      </Button>
    </Box>
  );
};

const CustomerTracking = ({ orderData, onAddMore }) => {
  const items = orderData?.items || [];
  const allReady = items.length > 0 && items.every((i) => i.status === 'Ready' || i.status === 'Completed');

  return (
    <Box sx={{ p: 3, maxWidth: 500, mx: 'auto' }}>
      {/* Status Header Card */}
      <Box sx={{ bgcolor: '#FFF', borderRadius: '16px', p: 3, textAlign: 'center', mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        {allReady ? (
          <>
            <CheckCircle sx={{ fontSize: 56, color: '#4CAF50', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} color="success.main">
              Order Ready!
            </Typography>
            <Typography fontSize="0.85rem" color="text.secondary" sx={{ mt: 0.5 }}>
              Your food is ready to be served
            </Typography>
          </>
        ) : (
          <>
            <Restaurant sx={{ fontSize: 48, color: '#4527A0', mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>
              Order #{orderData?.order_num}
            </Typography>
            {orderData?.estimated_time_remaining > 0 && (
              <Chip
                icon={<AccessTime />}
                label={`~${orderData.estimated_time_remaining} min remaining`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mt: 1.5 }}
              />
            )}
          </>
        )}
      </Box>

      {/* Order Items Table */}
      <Box sx={{ bgcolor: '#FFF', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F3FF' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#4527A0', border: 'none', py: 1.5 }}>Item</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#4527A0', border: 'none', py: 1.5 }}>Qty</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#4527A0', border: 'none', py: 1.5 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const statusColor =
                item.status === 'Ready' ? '#4CAF50' :
                item.status === 'Preparing' ? '#FF9800' :
                item.status === 'Rejected' ? '#F44336' : '#2196F3';

              return (
                <TableRow key={item.order_id} sx={{ bgcolor: index % 2 === 0 ? '#FAFAFA' : '#FFF', '& td': { border: 'none' } }}>
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography fontWeight={500} fontSize="0.85rem">{item.item_name}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">₹{item.amount}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5, fontSize: '0.85rem' }}>
                    {item.quantity}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        bgcolor: `${statusColor}20`,
                        color: statusColor,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Total Card */}
      {orderData?.total_amount && (
        <Box sx={{ bgcolor: '#FFF', borderRadius: '16px', p: 2.5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight={700} fontSize="1rem">Total</Typography>
            <Typography fontWeight={700} fontSize="1.1rem" color="#4527A0">₹{orderData.total_amount}</Typography>
          </Box>
        </Box>
      )}

      {!allReady && (
        <Button
          fullWidth
          variant="outlined"
          onClick={onAddMore}
          sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.2 }}
        >
          Add More Items
        </Button>
      )}
    </Box>
  );
};

// ═══ Main Page Component ═══

export const CustomerOrderPage = () => {
  const { slug, tableNo } = useParams();
  const [step, setStep] = useState('loading'); // loading, occupied, landing, menu, cart, waiting, tracking
  const [outletName, setOutletName] = useState('');
  const [outletId, setOutletId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [menuData, setMenuData] = useState({ items: [] });
  const [cart, setCart] = useState(() => loadCartFromStorage(slug, tableNo));
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cameFromTracking, setCameFromTracking] = useState(false);
  const pollRef = useRef(null);

  const tableNumber = parseInt(tableNo);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    saveCartToStorage(slug, tableNo, cart);
  }, [cart, slug, tableNo]);

  // Step 1: Check table status and load menu
  useEffect(() => {
    const checkTable = async () => {
      try {
        const data = await TableService.getTableStatus(slug, tableNumber);
        setOutletName(data.outlet_name || '');
        setOutletId(data.outlet_id || '');

        if (!data.is_valid) {
          setError('Invalid table');
          setStep('error');
        } else if (data.status === 'occupied') {
          setStep('occupied');
        } else {
          // Pre-load menu so it's ready after name entry
          try {
            const menu = await CustomerOrderService.getPublicMenu(slug);
            setMenuData(menu);
          } catch (menuErr) {
            // Menu load failure is not critical at this step
          }
          // Restore saved name if available
          const savedName = loadNameFromStorage(slug, tableNo);
          setCustomerName(savedName);
          setStep('landing');
        }
      } catch (err) {
        setError(err.message);
        setStep('error');
      }
    };
    checkTable();
  }, [slug, tableNumber, tableNo]);

  // Handle name submit → go directly to menu (no session created yet)
  const handleStartBrowsing = async (name) => {
    setSubmitting(true);
    try {
      setCustomerName(name);
      saveNameToStorage(slug, tableNo, name);
      // Load menu if not already loaded
      if (!menuData.items || menuData.items.length === 0) {
        const menu = await CustomerOrderService.getPublicMenu(slug);
        setMenuData(menu);
      }
      setStep('menu');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle "Send Order" from cart → create session only (order placed after approval)
  const handleSendOrder = async (cartItems, total) => {
    setSubmitting(true);
    try {
      // Create session (this sends the approval request to staff)
      const sessionData = await TableSessionService.createSession({
        outletSlug: slug,
        tableNumber,
        customerName,
      });
      setSessionId(sessionData.session_id);
      setCustomerId(sessionData.customer_id);

      // Cart stays in localStorage — order will be placed after staff approves
      // Go to waiting for approval
      setStep('waiting');
    } catch (err) {
      if (err.message.includes('occupied')) {
        setStep('occupied');
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Poll for session approval → place order → then move to tracking
  useEffect(() => {
    if (step !== 'waiting' || !sessionId) return;

    const poll = async () => {
      try {
        const data = await TableSessionService.getSessionStatus(sessionId);
        if (data.status === 'active') {
          clearInterval(pollRef.current);

          // NOW place the order since staff approved
          const cartItems = Object.entries(cart).length > 0
            ? cart
            : loadCartFromStorage(slug, tableNo);

          const items = Object.entries(cartItems)
            .filter(([, qty]) => qty > 0)
            .map(([itemId, qty]) => {
              const item = (menuData.items || []).find((i) => i.item_id === itemId);
              if (!item) return null;
              return {
                item_id: item.item_id,
                item_name: item.item_name,
                quantity: qty,
                amount: item.price * qty,
              };
            })
            .filter(Boolean);

          if (items.length > 0) {
            if (orderData?.order_num) {
              await CustomerOrderService.addItemsToOrder(orderData.order_num, {
                outletId,
                sessionId,
                items,
              });
            } else {
              await CustomerOrderService.placeOrder({
                outletId,
                items,
                sessionId,
                tableNumber,
                customerId,
              });
            }
          }

          // Clear cart from device after successful order placement
          setCart({});
          clearCartFromStorage(slug, tableNo);

          // Fetch order data for tracking
          const orderInfo = await CustomerOrderService.getOrderBySession(sessionId);
          setOrderData(orderInfo);
          setStep('tracking');
        } else if (data.status === 'cancelled') {
          clearInterval(pollRef.current);
          setError('Your order was declined by the staff.');
          setStep('error');
        }
      } catch (err) {
        // silently continue polling
      }
    };

    poll(); // immediate first check
    pollRef.current = setInterval(poll, 4000);
    return () => clearInterval(pollRef.current);
  }, [step, sessionId, slug, cart, menuData, outletId, tableNumber, customerId, orderData]);

  // Poll for order tracking updates
  useEffect(() => {
    if (step !== 'tracking' || !sessionId) return;

    const poll = async () => {
      try {
        const data = await CustomerOrderService.getOrderBySession(sessionId);
        setOrderData(data);
      } catch (err) {
        // silently continue
      }
    };

    poll();
    pollRef.current = setInterval(poll, 6000);
    return () => clearInterval(pollRef.current);
  }, [step, sessionId]);

  const handleAddItem = (item) => {
    setCart((prev) => ({ ...prev, [item.item_id]: (prev[item.item_id] || 0) + 1 }));
  };

  const handleRemoveItem = (itemId) => {
    setCart((prev) => {
      const newCart = { ...prev };
      if (newCart[itemId] > 1) {
        newCart[itemId]--;
      } else {
        delete newCart[itemId];
      }
      return newCart;
    });
  };

  const handleAddMore = () => {
    setCameFromTracking(true);
    setStep('menu');
  };

  const handleBackToTracking = () => {
    setCameFromTracking(false);
    setStep('tracking');
  };

  // ═══ Render ═══
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Gradient Header with outlet name & karobar logo */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #4527A0 0%, #7E57C2 50%, #FF6B35 100%)',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Typography fontWeight={700} fontSize="1.1rem" color="#FFF">
          {outletName || 'Restaurant'}
        </Typography>
        <Box
          component="img"
          src={karobarLogo}
          alt="Karobar"
          sx={{ height: 32, objectFit: 'contain' }}
        />
      </Box>

      {error && step === 'error' && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {error && step !== 'error' && (
        <Alert severity="error" onClose={() => setError('')} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Step content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {step === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        )}

        {step === 'occupied' && <TableOccupied outletName={outletName} />}

        {step === 'landing' && (
          <CustomerLanding
            outletName={outletName}
            tableNumber={tableNumber}
            onSubmit={handleStartBrowsing}
            loading={submitting}
            initialName={customerName}
          />
        )}

        {step === 'waiting' && <CustomerWaiting outletName={outletName} />}

        {step === 'menu' && (
          <CustomerMenu
            menuData={menuData}
            cart={cart}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            onViewCart={() => setStep('cart')}
            showBackButton={cameFromTracking}
            onBack={handleBackToTracking}
          />
        )}

        {step === 'cart' && (
          <CustomerCart
            cart={cart}
            items={menuData.items || []}
            onSendOrder={handleSendOrder}
            onBack={() => setStep('menu')}
            placing={submitting}
            customerName={customerName}
          />
        )}

        {step === 'tracking' && (
          <CustomerTracking orderData={orderData} onAddMore={handleAddMore} />
        )}
      </Box>
    </Box>
  );
};
